import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["control", "controlId", "disabled", "label", "size"];
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LabeledControlMixin } from '../../mixins/components/LabeledControlMixin';
import { LABELED_CONTROL_POSITIONS } from '../../constants/LABELED_CONTROL';
import { identifier } from '../../util/identifier';
import { FormFieldContext } from '../../js/FormFieldContext';
export var StyledLabeledControl = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledLabeledControl",
  componentId: "c11n-8-64-0__sc-1hafskn-0"
})(["", ""], LabeledControlMixin);
var defaultProps = {
  labelPosition: LABELED_CONTROL_POSITIONS.right,
  size: 'md'
};
/**
 * Compose selection control components ([`Radio`](#/Form%20components?id=radio),
 * [`Checkbox`](#/Form%20components?id=checkbox),
 * [`Switch`](#/Form%20components?id=switch)) with a [`Label`](#/Form%20components?id=label).
 *
 * @since 6.0.0
 */

var LabeledControl = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _defaultProps$props = Object.assign({}, defaultProps, props),
      control = _defaultProps$props.control,
      controlId = _defaultProps$props.controlId,
      disabled = _defaultProps$props.disabled,
      label = _defaultProps$props.label,
      size = _defaultProps$props.size,
      rest = _objectWithoutProperties(_defaultProps$props, _excluded);

  var context = Object.assign({}, React.useContext(FormFieldContext), {
    // Create a default id to be used with the control and labels
    controlId: React.useRef(identifier()).current
  });

  if (typeof controlId !== 'undefined') {
    context.controlId = controlId;
  }

  if (typeof disabled !== 'undefined') {
    context.disabled = disabled;
  }

  if (typeof props.size !== 'undefined') {
    context.size = props.size;
  }

  var merged = Object.assign({}, defaultProps, context, props); // Note: selection control components should come first in the DOM tree so that
  // labels can trigger off :hover/:focus states.

  return /*#__PURE__*/React.createElement(StyledLabeledControl, _extends({
    ref: ref,
    size: merged.size
  }, rest), /*#__PURE__*/React.createElement(FormFieldContext.Provider, {
    value: context
  }, control, label));
});
LabeledControl.displayName = 'LabeledControl';
LabeledControl.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A [`Label`](#/Form%20components?id=label) component.
   */
  label: PropTypes.node.isRequired,

  /**
   * A [`Checkbox`](#/Form%20components?id=checkbox) or
   * [`Radio`](#/Form%20components?id=radio) component.
   */
  control: PropTypes.node.isRequired,

  /**
   * Identifier used for form controls and labels.
   * This will set `id` for form controls and `htmlFor` for
   * [`Label`](#/Form%20components?id=label) components.
   */
  controlId: PropTypes.string,

  /**
   * Set the control as disabled.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   *
   * @since 6.7.0
   */
  disabled: PropTypes.bool,

  /**
   * If `true`, this will span the entire width. Be careful: while appealing at mobile sizes,
   * this may result in too much space between the label and input at larger widths.
   */
  fluid: PropTypes.bool,

  /** Where to align the label with respect to the selection control */
  labelPosition: PropTypes.oneOf(Object.keys(LABELED_CONTROL_POSITIONS)),

  /**
   * `size` prop passed to the `Label` and control. This is mostly reserved for Switches.
   *
   * @since 6.7.0
   */
  size: PropTypes.oneOf(['md', 'lg'])
} : {};
export default LabeledControl;